import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { UserJourney } from '@/components/shared/UserJourney';
import { Button } from '@/components/shared/Button';
import { Users, Share2, Eye, MessageCircle, Instagram, Loader2 } from 'lucide-react';
import { ENTITY_CONFIGS } from '@/lib/entity-config';
import { cn } from '@/lib/cn';

const API_BASE_URL = 'https://mvc-api.lancetkeith.workers.dev';

interface ClosetItem {
  id: string;
  asset_id: string;
  image_url: string;
  name: string;
  category: string;
}

interface SocialStats {
  likes: number;
  comments: number;
  shares: number;
}

const STYLE_CATEGORIES = [
  { id: 'urban', label: 'Urban Loft', icon: '🏙️', active: true },
  { id: 'minimalist', label: 'Minimalist Studio', icon: '✨' },
  { id: 'vintage', label: 'Vintage Boutique', icon: '🎨' },
  { id: 'luxury', label: 'Luxury Lounge', icon: '💎' },
  { id: 'streetwear', label: 'Streetwear Space', icon: '🎯' },
];

export function MVCCloset() {
  const entity = ENTITY_CONFIGS.mvc;
  const navigate = useNavigate();
  const location = useLocation();

  const [closetItems, setClosetItems] = useState<ClosetItem[]>([]);
  const [featuredItem, setFeaturedItem] = useState<ClosetItem | null>(null);
  const [socialStats, setSocialStats] = useState<SocialStats>({ likes: 0, comments: 0, shares: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has purchased from AL3Viewer
    const hasPurchase = document.cookie.split('; ').find(row => row.startsWith('al3v_purchase='));
    const state = location.state as {
      purchased_asset_id?: string;
      license_type?: string;
      purchased_asset?: ClosetItem;
    } | null;

    if (!hasPurchase && !state?.purchased_asset_id) {
      // Redirect to AL3Viewer if no purchase cookie found
      alert('Please purchase a license from AL3Viewer first to access My Virtual Closet');
      navigate('/al3viewer');
      return;
    }

    // If we have the purchased asset data directly, use it immediately
    if (state?.purchased_asset) {
      console.log('Received purchased asset from AL3Viewer:', state.purchased_asset);
      setFeaturedItem(state.purchased_asset);
      setClosetItems([state.purchased_asset]);
      setLoading(false);
    } else {
      // Otherwise fetch closet data from API
      fetchClosetData(state?.purchased_asset_id);
    }
  }, [navigate, location]);

  const fetchClosetData = async (purchased_asset_id?: string) => {
    try {
      setLoading(true);

      // Fetch user's closet items
      const closetResponse = await fetch(`${API_BASE_URL}/api/mvc/closet/user-temp-id`);
      if (closetResponse.ok) {
        const closetData = await closetResponse.json();
        if (closetData.success && closetData.items) {
          setClosetItems(closetData.items);

          // Set featured item (newly purchased or first item)
          if (purchased_asset_id) {
            const purchased = closetData.items.find((item: ClosetItem) => item.asset_id === purchased_asset_id);
            setFeaturedItem(purchased || closetData.items[0]);
          } else {
            setFeaturedItem(closetData.items[0]);
          }
        }
      }

      // Fetch social stats for featured item
      if (featuredItem?.asset_id) {
        const socialResponse = await fetch(`${API_BASE_URL}/api/mvc/social/${featuredItem.asset_id}`);
        if (socialResponse.ok) {
          const socialData = await socialResponse.json();
          if (socialData.success) {
            setSocialStats({
              likes: socialData.likes || 0,
              comments: socialData.comments || 0,
              shares: socialData.shares || 0
            });
          }
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching closet data:', err);
      // Use fallback mock data
      setClosetItems([
        { id: '1', asset_id: 'mock-1', image_url: '', name: 'Athletic Runner', category: 'Sneakers' },
        { id: '2', asset_id: 'mock-2', image_url: '', name: 'Chelsea Boot', category: 'Boots' },
        { id: '3', asset_id: 'mock-3', image_url: '', name: 'Stiletto Heel', category: 'Heels' },
        { id: '4', asset_id: 'mock-4', image_url: '', name: 'Oxford Dress', category: 'Dress Shoes' },
        { id: '5', asset_id: 'mock-5', image_url: '', name: 'Hiking Boot', category: 'Boots' },
        { id: '6', asset_id: 'mock-6', image_url: '', name: 'Sandal Strappy', category: 'Sandals' },
      ]);
      setFeaturedItem({ id: '1', asset_id: 'mock-1', image_url: '', name: 'Athletic Leather Runner', category: 'Sneakers' });
      setSocialStats({ likes: 2400, comments: 342, shares: 89 });
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!featuredItem) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/mvc/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_id: featuredItem.asset_id,
          user_id: 'user-temp-id',
          platform: 'instagram'
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Shared to Instagram successfully!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      alert('Share feature coming soon!');
    }
  };

  const headerActions = (
    <>
      <Button variant="gradient-purple" size="lg">
        <Users className="h-4 w-4" />
        Style Curator
      </Button>
      <Button variant="gradient" size="lg" onClick={handleShare}>
        <Share2 className="h-4 w-4" />
        Share Closet
      </Button>
    </>
  );

  if (loading) {
    return (
      <div className="flex h-screen flex-col bg-ck-dark">
        <Header entity={entity} actions={headerActions} onClose={() => navigate('/')} />
        <UserJourney currentStep="mvc" sessionId="2024-0847" />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-ck-cyan" />
            <p className="mt-4 text-lg font-medium text-muted-foreground">Loading your virtual closet...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-ck-dark">
      {/* Header */}
      <Header entity={entity} actions={headerActions} onClose={() => navigate('/')} />

      {/* User Journey Nav */}
      <UserJourney currentStep="mvc" sessionId="2024-0847" />

      {/* Responsive: True vertical stack on mobile, side-by-side on desktop */}
      <div className="flex flex-1 flex-col lg:grid lg:grid-cols-[1fr_320px] lg:overflow-hidden">
        {/* Left side - scrollable content */}
        <main className="flex-1 overflow-y-auto lg:order-1">
        {/* Style Categories */}
        <div className="border-b border-ck-dark-border bg-black px-4 py-3 md:px-6 md:py-4">
          <div className="flex flex-wrap gap-2 md:gap-3">
            {STYLE_CATEGORIES.map(category => (
              <button
                key={category.id}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all md:gap-2 md:px-4 md:py-2 md:text-sm',
                  category.active
                    ? 'bg-gradient-to-r from-ck-cyan to-ck-purple text-white'
                    : 'bg-ck-dark-card text-muted-foreground hover:bg-ck-dark-card/70'
                )}
              >
                <span>{category.icon}</span>
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Featured Design */}
        <div className="border-b border-ck-dark-border bg-gradient-to-br from-ck-dark via-ck-dark-card to-ck-dark p-6 md:p-8 lg:p-12">
          <div className="mx-auto max-w-4xl">
            <p className="mb-2 text-center text-sm font-medium uppercase tracking-wider text-ck-cyan">
              Featured Design
            </p>
            <h2 className="mb-2 text-center text-4xl font-bold text-foreground">
              {featuredItem?.name || 'Athletic Leather Runner'}
            </h2>
            <div className="mb-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>CK SAaS Collection</span>
              <span>•</span>
              <span>{featuredItem?.category || 'Custom Design'}</span>
            </div>

            {/* Featured item image */}
            <div className="mb-4 rounded-lg overflow-hidden bg-gradient-to-br from-ck-cyan/20 to-ck-purple/20 md:mb-6">
              {featuredItem?.image_url ? (
                <img
                  src={featuredItem.image_url}
                  alt={featuredItem.name}
                  className="w-full h-auto object-contain"
                />
              ) : (
                <div className="flex h-48 items-center justify-center md:h-56 lg:h-64">
                  <div className="text-6xl md:text-7xl lg:text-8xl">👟</div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              <Button variant="gradient-purple" size="default" className="gap-2 md:h-12 md:px-6">
                <Eye className="h-4 w-4 md:h-5 md:w-5" />
                <span className="text-xs md:text-base">Virtual Try-On</span>
              </Button>
              <Button variant="outline" size="default" className="gap-2 border-ck-dark-border md:h-12 md:px-6">
                <span>✏️</span>
                <span className="text-xs md:text-base">Customize</span>
              </Button>
              <Button variant="outline" size="default" className="gap-2 border-ck-dark-border md:h-12 md:px-6">
                <span>👕</span>
                <span className="text-xs md:text-base">Create Outfit</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Your Dream Collection */}
        <div className="bg-ck-dark p-4 md:p-6 lg:p-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-ck-purple">Your Dream Collection</h3>
              <p className="text-sm text-muted-foreground">{closetItems.length} items</p>
            </div>
            <Button variant="gradient-purple">View All</Button>
          </div>

          <div className="grid grid-cols-3 gap-3 md:grid-cols-4 md:gap-4 lg:grid-cols-6">
            {closetItems.slice(0, 6).map((item, idx) => (
              <div
                key={item.id}
                className={cn(
                  'flex aspect-square items-center justify-center rounded-lg border-2 transition-all hover:scale-105 cursor-pointer',
                  idx === 0
                    ? 'border-ck-purple bg-ck-purple/10'
                    : 'border-ck-dark-border bg-ck-dark-card hover:border-ck-cyan'
                )}
                onClick={() => setFeaturedItem(item)}
              >
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="h-full w-full object-cover rounded-lg" />
                ) : (
                  <div className="text-6xl">👟</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Social Stats & AI Suggestions */}
        <div className="border-t border-ck-dark-border bg-ck-dark-card">
          <div className="grid grid-cols-3 divide-x divide-ck-dark-border">
            {/* Social Stats */}
            <div className="p-6">
              <h4 className="mb-4 text-lg font-semibold text-ck-cyan">Social</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="text-xl">❤️</span>
                    <span>{socialStats.likes >= 1000 ? `${(socialStats.likes / 1000).toFixed(1)}K` : socialStats.likes} likes</span>
                  </div>
                  <Button variant="gradient-purple" size="sm">
                    Like
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MessageCircle className="h-5 w-5" />
                    <span>{socialStats.comments} comments</span>
                  </div>
                  <Button variant="outline" size="sm">
                    View
                  </Button>
                </div>
                <Button variant="gradient" size="lg" className="w-full gap-2" onClick={handleShare}>
                  <Instagram className="h-4 w-4" />
                  Share to Instagram
                </Button>
              </div>
            </div>

            {/* AI Perfect Match */}
            <div className="p-6">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xl">✨</span>
                <h4 className="text-lg font-semibold text-ck-cyan">Perfect Match</h4>
              </div>
              <p className="mb-4 text-sm text-foreground">
                Pair with slim black joggers and oversized hoodie for urban edge
              </p>
            </div>

            {/* Outfit Suggestions */}
            <div className="p-6">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xl">👔</span>
                <h4 className="text-lg font-semibold text-ck-cyan">Outfit Suggestions</h4>
              </div>
              <p className="mb-2 text-sm text-muted-foreground">3 complete looks ready</p>
              <Button variant="gradient-purple" className="w-full">
                View All
              </Button>
            </div>
          </div>
        </div>

        {/* Living the Dream Footer */}
        <div className="border-t border-ck-dark-border bg-gradient-to-r from-ck-purple/20 to-ck-cyan/20 p-4">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">😊</span>
            <div>
              <p className="text-sm font-semibold text-ck-purple">Living the Dream</p>
              <p className="text-xs text-muted-foreground">Your virtual closet awaits</p>
            </div>
          </div>
        </div>
        </main>
      </div>

      {/* Mobile AI Assistant - Fixed bottom panel */}
      <div className="max-h-[40vh] overflow-y-auto border-t border-ck-dark-border bg-black p-4 lg:hidden">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-ck-cyan">AI Styling Assistant</h3>
          <div className="rounded-lg bg-ck-dark-card p-3">
            <p className="text-sm text-foreground">
              Perfect Match! Pair with slim black joggers and oversized hoodie for urban edge
            </p>
          </div>
          <input
            type="text"
            placeholder="Ask for styling suggestions..."
            className="w-full rounded-lg bg-ck-dark px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ck-cyan"
          />
        </div>
      </div>
    </div>
  );
}